import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { DSAlert } from '@demandstar/components/alert';
import { Status } from '@demandstar/components/constants';

import { initialInstantBidData } from 'src/store/reducers/addbid';
import { AddBidInfo, UploadedFile } from 'src/types/addbid';
import { CommodityMap } from 'src/types/commodity';
import { useSubmitInstantBidFile } from 'src/query/queries/addinstantbid/submitInstantBidFile';
import {
  getAddBidBidTypes,
  getAddBidBidWriters,
  getInstantBidSuppliers,
  getSavedLegal,
  setAddBidDetails,
  submitAddInstantBidInfo,
} from 'src/store/actions';
import { useGetCommodityCodesCognitiveSearch } from 'src/query/queries/cognitiveSearch/getcommoditiyCognitiveSearchData';
import { mergeCommodityCodes } from 'src/components/products/helpers';

import { useAuthContext } from 'src/auth/AuthProvider';
import { getJobResult, getJobStatus } from 'src/store/services';

import { FileUploadBox } from './FileUploadBox';
import { UploadProgressBar } from './UploadProgressBar';
import { InstantBidExtractedForm } from './InstantBidExtractedForm';
import { DSButton } from '../../../../library/button';
import { useDSSelector } from '../../../../store/reducers';
import { queryClient } from '../../../../queryClient';
import {
  getCommodityQueryValue,
  transformInstantBidData,
  transformToAddBidInfo,
} from '../../../../utils/helpers/addInstantBidHelper';
import { useParams } from 'react-router-dom';
import DuplicateInstantBid from './duplicateInstantBid';
import { ModalPopUp } from 'src/components/common/modals';
import { AppContext } from 'src/global-context/AppContext';
import { toastFn } from 'src/utils/helpers';
import { addBidToastId } from 'src/store/sagas/addbid';
import history from 'src/utils/history';

export function AddInstantBid() {
  const params = useParams() as any;
  const editBidId = parseInt(params?.bidId);

  const [instantBidFile, setInstantBidFile] = useState<UploadedFile[]>([]);
  const [fileName, setFileName] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [fileError, setFileError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [jobId, setJobId] = useState(0);
  const [status, setStatus] = useState<string>('start');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [showData, setShowData] = useState(false);

  const [formData, setFormData] = useState(initialInstantBidData);
  const [searchQuery, setSearchQuery] = useState('');
  const [checkedCommodities, setCheckedCommodities] = useState([]);
  const [selectedCommodityIds, setSelectedCommodityIds] = useState([]);
  const [hasInitializedRelevantCodes, setHasInitializedRelevantCodes] = useState(false);
  const [manualAddedCommodities, setManualAddedCommodities] = useState<any[]>([]);
  const [removedCommodityIds, setRemovedCommodityIds] = useState<string[]>([]);
  const [showAddcommodityPopUp, setShowAddcommodityPopUp] = useState(false);

  const [currentPageSupplier, setCurrentPageSupplier] = useState(1);
  const supplierPerPage = 10;
  const bidLegalAds = useDSSelector(state => state.accountinfo.savedLegalData);

  const dispatch = useDispatch();
  const { auth } = useAuthContext();
  const memberId = auth?.memberId;
  const {
    instantBidSuppliers,
    instantBidSuppliersLoading,
    bidwriters,
    bidtypes,
    duplicateInstantBidModel,
  } = useDSSelector(s => s.addbid);
  const commoditydata = queryClient.getQueryData<any>(['CommodityCodes'])?.data?.result || [];

  const { mutate: submitInstantBidFile } = useSubmitInstantBidFile({});
  const { data: commodityCodesData, isFetching: isFetchingCommodityCodes } =
    useGetCommodityCodesCognitiveSearch(searchQuery, { enabled: searchQuery?.length > 2 });

  const mergedCodes = useMemo(() => {
    const cognitiveCodes = mergeCommodityCodes(commodityCodesData, []) as any[];
    const combined = [...manualAddedCommodities, ...cognitiveCodes];
    return Array.from(new Map(combined.map(item => [String(item.commodityid), item])).values());
  }, [commodityCodesData, manualAddedCommodities]);
  const { changeInstantBidview } = useContext(AppContext);
  const [reqDocIds, setReqDocIds] = useState("");
  const { jobId: jobid } = useParams<{ jobId: string }>();

  const validateAndSetFile = (files: FileList | null) => {
    if (!files) return;
    const file = files[0];
    if (file.type !== 'application/pdf') {
      setFileError('Only PDF files are supported right now');
      setFileName('');
      setInstantBidFile([]);
      return;
    }
    setFileError('');
    setFileName(file.name);
    setInstantBidFile([file as any]);
  };

  const resetFile = () => {
    setShowData(false);
    setError('');
    setFileError('');
    setFileName('');
    setInstantBidFile([]);
    setFormData(initialInstantBidData);
    setStatus('');
    setProgress(0);
    setCheckedCommodities([]);
    setHasInitializedRelevantCodes(false);
    setJobId(0);
  };

  const handleSubmit = () => {
    setError('');
    if (!instantBidFile.length) return;
    setStatus('uploading');
    setProgress(0);
    submitInstantBidFile(
      { file: instantBidFile[0], memberId: memberId},
      {
        onSuccess: res => {
          setJobId(res.job_id);
          history.push(`/buyers/AddInstantBid/${res.job_id}`);
          setStatus('pending');
          setError('');
          setProgress(25);
        },
        onError: () => {
          setError('Failed to submit file. Please try again.');
          setStatus('');
          resetFile();
        },
      },
    );
  };

  const handleCheckboxChange = (commodity: any) => {
    setCheckedCommodities(prev => {
      const exists = prev.some((item: any) => item.commodityid === commodity.commodityid);
      return exists
        ? prev.filter((item: any) => item.commodityid !== commodity.commodityid)
        : [...prev, commodity];
    });
  };

  const handleManualCommodityProceed = (selectedCommodities: CommodityMap[]) => {
    const popupCommodities = selectedCommodities.map(c => ({
      commodityid: c.commodityId,
      commodity: c.commodityDescription,
      fullcode: c.formattedCode,
      formattedCode: c.formattedCode,
    }));

    const cognitiveCodes = (mergeCommodityCodes(commodityCodesData, []) as any[]).map((c: any) => ({
      commodityid: c.commodityid,
      commodity: c.commodity,
      fullcode: c.fullcode,
      formattedCode: c.formattedCode || c.fullcode || '',
    }));

    const popupIds = popupCommodities.map(item => String(item.commodityid));
    const cognitiveIds = cognitiveCodes.map(item => String(item.commodityid));
    const removedIds = cognitiveIds.filter(id => !popupIds.includes(id));

    setRemovedCommodityIds(removedIds);

    const manuallyAdded = popupCommodities.filter(
      item => !cognitiveIds.includes(String(item.commodityid)),
    );
    setManualAddedCommodities(manuallyAdded);

    const finalChecked = [
      ...cognitiveCodes.filter(item => !removedIds.includes(String(item.commodityid))),
      ...manuallyAdded,
    ];
    const deduped = Array.from(
      new Map(finalChecked.map(item => [String(item.commodityid), item])).values(),
    );

    setCheckedCommodities(deduped);
    setSelectedCommodityIds(deduped.map((item: any) => item.commodityid));
    setShowAddcommodityPopUp(false);
  };
  const handleFormSubmit = (value: any) => {
    if(checkedCommodities.length > 0){
      const ids = checkedCommodities.map(item => String(item.commodityid));
      const matchedItems = commoditydata.filter(item => ids.includes(String(item.commodityId)));
      const addBidInfo: AddBidInfo = transformToAddBidInfo(value);
      const instantBidPayload = {
        ...addBidInfo,
        commodities: matchedItems,
        instantBidFile: instantBidFile[0],
        legalAds: bidLegalAds,
        instantBidFileName: fileName,
        documentId:reqDocIds,
      };
       dispatch(setAddBidDetails({ addInstantBid: instantBidPayload }));
       dispatch(submitAddInstantBidInfo({ data: instantBidPayload }));
       changeInstantBidview(true);
    }
    else{
      toastFn('error', 'Should select atleast one commodity', addBidToastId);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      const jobID = Number(jobid);
      if (jobID !== 0 && !Number.isNaN(jobID)) {
        setJobId(jobID);
        setStatus('pending');
        setProgress(28);
        const res = await getJobStatus(jobID);
        setStatus(res.status);
        setFileName(res.filename);
      }
    };
    fetchData();
  }, [jobid]);

  useEffect(() => {
    return () => {
      dispatch(setAddBidDetails({ ResetState: true }));
    };
  }, [dispatch]);
  const isProcessing = ['uploading', 'pending', 'processing'].includes(status);
  useEffect(() => {
    if (status === 'completed' && mergedCodes.length > 0 && !hasInitializedRelevantCodes) {
      const initial = mergedCodes.map((c: any) => ({
        commodityid: c.commodityid,
        commodity: c.commodity,
        fullcode: c.fullcode,
        formattedCode: c.formattedCode || c.fullcode || '',
      }));
      setCheckedCommodities(initial);
      setHasInitializedRelevantCodes(true);
    }
  }, [status, mergedCodes, hasInitializedRelevantCodes]);

  useEffect(() => {
    if (jobId === 0) return;

    let finalProgressInterval: any = null;

    const interval = setInterval(async () => {
      try {
        const res = await getJobStatus(jobId);
        setStatus(res.status);
        if(!fileName){
          setFileName(res.filename);
        }
        if (res.status === 'processing') {
          setProgress(prev => (prev < 50 ? 50 : prev));
          if (res.processed_questions === res.total_questions) {
            setProgress(prev => (prev < 75 ? 75 : prev));
            if (!finalProgressInterval) {
              finalProgressInterval = setInterval(() => {
                setProgress(prev => {
                  if (prev >= 96) {
                    clearInterval(finalProgressInterval);
                    finalProgressInterval = null;
                    return prev;
                  }
                  return prev + Math.random() * 2;
                });
              }, 1300);
            }
          }
        }

        if (res.status === 'completed') {
          if (finalProgressInterval) {
            clearInterval(finalProgressInterval);
            finalProgressInterval = null;
          }
          setProgress(100);
          const response = await getJobResult(jobId);
          const final = transformInstantBidData(response?.extracted_data);
          setReqDocIds(final.reqDocIds);
          setSearchQuery(getCommodityQueryValue(final?.commodities, final?.relatedtags));
          setFormData(final);
          setShowData(true);
          dispatch(setAddBidDetails({ instantBidData: final, showInstantBid: true }));
          clearInterval(interval);
        }

        if (res.status === 'failed') {
          history.goBack();
          if (finalProgressInterval) clearInterval(finalProgressInterval);
          resetFile();
          setError(
            'Job failed. Please re-upload the file and try again. If the issue persists, contact support with the Bid Document.',
          );
          clearInterval(interval);
        }
      } catch (err: any) {
        resetFile();
        setError(err?.message);
        clearInterval(interval);
      }
    }, 10000);

    return () => {
      clearInterval(interval);
      if (finalProgressInterval) clearInterval(finalProgressInterval);
    };
  }, [jobId, dispatch, fileName]);

  useEffect(() => {
    let interval: any;

    if (status === 'uploading') {
      interval = setInterval(() => {
        setProgress(prev => (prev >= 21 ? prev : prev + Math.random() * 2));
      }, 1500);
    }
    if (status === 'pending') {
      setProgress(25);
      interval = setInterval(() => {
        setProgress(prev => (prev >= 45 ? prev : prev + Math.random() * 2));
      }, 1300);
    }
    if (status === 'processing') {
      setProgress(prev => (prev < 50 ? 50 : prev));
      interval = setInterval(() => {
        setProgress(prev => (prev >= 71 ? prev : prev + Math.random() * 2));
      }, 4000);
    }
    if (status === 'completed') setProgress(100);

    return () => clearInterval(interval);
  }, [status]);

  useEffect(() => {
    const ids = checkedCommodities.map((item: any) => item.commodityid);
    setSelectedCommodityIds(ids);
    if (status === 'completed' && ids.length > 0) {
      dispatch(getInstantBidSuppliers({ commodityIds: ids }));
    }
  }, [checkedCommodities, status, dispatch]);

  useEffect(() => {
    if (!editBidId) {
      dispatch(setAddBidDetails({ ResetState: true }));
    }
  }, [dispatch, editBidId]);

  useEffect(() => {
    if (bidwriters.length === 0) dispatch(getAddBidBidWriters({}));
    if (bidtypes.length === 0) dispatch(getAddBidBidTypes({}));
    dispatch(getSavedLegal());
  }, [bidtypes.length, bidwriters.length, dispatch]);
  return (
    <>
      <div className='container'>
        <div
          className='card min-vh-100 p-4'
          onDrop={e => {
            e.preventDefault();
            setIsDragging(false);
            validateAndSetFile(e.dataTransfer.files);
          }}
          onDragOver={e => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
        >
          <h2 className='arrowWrapper mr-auto mt-4'>Add Instant Bid with OpenBids AI</h2>
          <p className='upload-description'>
            Simply upload your bid specification document and let our AI instantly analyze it. Key
            bid details will be automatically extracted and populated into the form, helping you
            create bids faster, smarter, and with greater accuracy.
          </p>

          <div className='mt-4'>
            <FileUploadBox
              fileName={fileName}
              isDragging={isDragging}
              fileError={fileError}
              fileInputRef={fileInputRef}
              onDragOver={e => {
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={e => {
                e.preventDefault();
                setIsDragging(false);
                validateAndSetFile(e.dataTransfer.files);
              }}
              onFileChange={validateAndSetFile}
              onReset={resetFile}
            />

            <UploadProgressBar status={status} progress={progress} />

            {status === 'completed' && (
              <div className='text-center my-4'>
                <span className='mr-2 text-success mdi mdi-check-bold' />
                Successfully Extracted Bid Details!
                <small> Analysis Completed</small>
              </div>
            )}

            {!showData && !isProcessing && (
              <div className='text-right mt-3'>
                <DSButton onClick={handleSubmit} inactive={!fileName}>
                  Upload &amp; Extract
                </DSButton>
              </div>
            )}

            {showData && status === 'completed' && (
              <InstantBidExtractedForm
                formData={formData}
                mergedCodes={mergedCodes}
                checkedCommodities={checkedCommodities}
                isFetchingCommodityCodes={isFetchingCommodityCodes}
                suppliers={instantBidSuppliers}
                suppliersLoading={instantBidSuppliersLoading}
                currentPage={currentPageSupplier}
                supplierPerPage={supplierPerPage}
                selectedCommodityIds={selectedCommodityIds}
                showAddcommodityPopUp={showAddcommodityPopUp}
                commoditydata={commoditydata}
                onCheckboxChange={handleCheckboxChange}
                onAddManually={() => setShowAddcommodityPopUp(true)}
                onPageChange={setCurrentPageSupplier}
                onFormSubmit={handleFormSubmit}
                onManualCommodityProceed={handleManualCommodityProceed}
                onManualCommodityCancel={() => setShowAddcommodityPopUp(false)}
                bidTypes={bidtypes}
                bidWriters={bidwriters}
                auth={auth}
              />
            )}

            {error && <DSAlert type={Status.Error}>{error}</DSAlert>}
          </div>
        </div>
      </div>
      <ModalPopUp title='Duplicate Bid' closeModal={false} isOpen={duplicateInstantBidModel}>
        <DuplicateInstantBid />
      </ModalPopUp>
    </>
  );
}
